import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { 
  Laptop, 
  Server, 
  MapPin, 
  Users, 
  Database, 
  Globe, 
  Smartphone, 
  CheckCircle2, 
  Info,
  ArrowRight
} from 'lucide-react';
import { projectId } from '../utils/supabase/info';
import { toast } from 'sonner';

import { bearerAuthHeader } from '../utils/apiHeaders';
interface AssetSchemaTemplate {
  id: string;
  name: string;
  description: string;
  complexity: "lite" | "standard" | "enterprise";
  schema: {
    objectTypes: any[];
  };
}

interface AssetSchemaSelectorProps {
  companyId: string;
  onSelect: (template: AssetSchemaTemplate) => void;
  onCancel: () => void;
}

export function AssetSchemaSelector({ companyId, onSelect, onCancel }: AssetSchemaSelectorProps) {
  const [templates, setTemplates] = useState<AssetSchemaTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await fetch(`https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/asset-schemas/templates`, {
        headers: {
          ...bearerAuthHeader()
        }
      });
      if (response.ok) {
        const data = await response.json();
        setTemplates(data.templates);
      }
    } catch (error) {
      console.error('Error fetching asset templates:', error);
      toast.error('Failed to load asset templates');
    } finally {
      setLoading(false);
    }
  };

  const handleApply = () => {
    const template = templates.find(t => t.id === selectedId);
    if (template) {
      onSelect(template);
    }
  };

  const renderIcon = (iconName: string) => {
    const icons: any = {
      Laptop, Server, MapPin, Users, Database, Globe, Smartphone, Disc: Database, Briefcase: Globe, HardDrive: Server
    };
    const Icon = icons[iconName] || Laptop;
    return <Icon className="h-4 w-4" />;
  };

  return (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <h2 className="text-2xl font-bold">Choose a CMDB Starter Template</h2>
        <p className="text-muted-foreground max-w-2xl mx-auto">
          Don't start from scratch. Select a best-practice schema foundation that matches your client's maturity level.
        </p>
      </div>

      {loading ? (
        <div className="flex justify-center p-8">Loading templates...</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {templates.map((template) => (
            <Card 
              key={template.id}
              className={`cursor-pointer transition-all border-2 ${
                selectedId === template.id 
                  ? 'border-primary ring-2 ring-primary/20' 
                  : 'border-transparent hover:border-gray-200 bg-gray-50/50'
              }`}
              onClick={() => setSelectedId(template.id)}
            >
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle className="text-xl">{template.name}</CardTitle>
                    <Badge variant="secondary" className="mt-2">
                      {template.complexity === 'lite' ? 'Basic Discovery' : 'ITIL CSDM Aligned'}
                    </Badge>
                  </div>
                  {selectedId === template.id && (
                    <CheckCircle2 className="h-6 w-6 text-primary" />
                  )}
                </div>
                <CardDescription className="mt-2 text-base">
                  {template.description}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="text-sm font-medium text-muted-foreground">Included Object Types:</div>
                  <div className="grid grid-cols-2 gap-2">
                    {template.schema.objectTypes.map((ot: any) => (
                      <div key={ot.id} className="flex items-center gap-2 p-2 bg-white rounded border text-sm">
                        {renderIcon(ot.icon)}
                        <span>{ot.name}</span>
                      </div>
                    ))}
                  </div>
                  
                  {template.complexity === 'enterprise' && (
                    <div className="mt-4 p-3 bg-blue-50 text-blue-800 text-sm rounded-md flex gap-2">
                      <Info className="h-4 w-4 shrink-0 mt-0.5" />
                      <div>
                        <strong>Discovery Ready:</strong> Includes pre-configured mapping logic for Lansweeper and Axonius.
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <div className="flex justify-end gap-3 pt-4 border-t">
        <Button variant="ghost" onClick={onCancel}>Cancel</Button>
        <Button 
          onClick={handleApply} 
          disabled={!selectedId}
          className="gap-2"
        >
          Use Selected Template <ArrowRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
